import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { IuguCreditCard, IuguResponse } from "ngx-iugu";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject, Observable, Subject, combineLatest } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, mergeMap, takeUntil, tap } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { AffiliateDTO } from "src/app/data/schema/Billing/Affiliate/AffiliateDTO";
import { CreditCardResponse } from "src/app/data/schema/Billing/Asaas/CreditCardResponse";
import { BillingProviderEnumDTO } from "src/app/data/schema/Billing/BillingProviderEnumDTO";
import { CreditCardCreateDTO } from "src/app/data/schema/Billing/Customer/CreditCardCreateDTO";
import { CreditCardIssuerDTO } from "src/app/data/schema/Billing/Customer/CreditCardIssuerDTO";
import { CustomerGatewaySettingDTO } from "src/app/data/schema/Billing/Customer/CustomerGatewaySettingDTO";
import { AffiliatesFilterDTO } from "src/app/data/schema/Billing/Filter/AffiliatesFilterDTO";
import { AsaasTokenizeModel } from "src/app/data/schema/Billing/Models/AsaasTokenizeModel";
import { ApiAffiliateService } from "src/app/data/service/billing/api-affiliate.service";
import { ApiBillingCustomerService } from "src/app/data/service/billing/api-billing-customer.service";
import { ApiIuguService } from "src/app/data/service/iugu/api-iugu.service";
import { CreditCardData } from "src/app/modules/new-payment/new-payment/models/CreditCardData";
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from "src/app/shared/component/confirm-dialog/confirm-dialog.component";
import { NewCreditCardDialogComponent } from "src/app/shared/component/new-credit-card-dialog/new-credit-card-dialog.component";
import { HttpErrorResponseUtils } from "src/app/utils/HttpErrorResponseUtils";

@Component({
    selector: "app-profile-payment-methods",
    templateUrl: "./profile-payment-methods.component.html",
    styleUrls: ["./profile-payment-methods.component.scss"]
})
export class ProfilePaymentMethodsComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject<any>();

    public affiliates: AffiliateDTO[];
    public selectedAffiliate = new BehaviorSubject<AffiliateDTO | null>(null);
    public selectedGateway = new BehaviorSubject<BillingProviderEnumDTO | null>(null);
    public customerGatewaySettings: CustomerGatewaySettingDTO | null = null;

    // Adiciona essas novas propriedades para ngModel
    public selectedAffiliateValue: AffiliateDTO | null = null;
    public selectedGatewayValue: BillingProviderEnumDTO | null = null;

    affiliateFilter$ = new BehaviorSubject<string>("");

    constructor(
        private AffiliateService: ApiAffiliateService,
        private BillingCustomerService: ApiBillingCustomerService,
        private translate: TranslateService,
        private authService: AuthService,
        private spinner: NgxSpinnerService,
        private _apiIuguService: ApiIuguService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.selectedAffiliate.subscribe((affiliate) => (this.selectedAffiliateValue = affiliate));
        this.selectedGateway.subscribe((gateway) => (this.selectedGatewayValue = gateway));

        combineLatest([this.selectedAffiliate, this.selectedGateway])
            .pipe(
                debounceTime(150),
                filter(([affiliate, gateway]) => !!affiliate && !!gateway),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.loadGatewaySettings();
            });

        this.affiliateFilter$
            .pipe(debounceTime(150), distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((search) => this.listAffiliates(search));
    }

    compareAffiliate(affiliate1: AffiliateDTO, affiliate2: AffiliateDTO): boolean {
        return affiliate1 && affiliate2 ? affiliate1.id === affiliate2.id : affiliate1 === affiliate2;
    }

    loadGatewaySettings(): void {
        const affiliate = this.selectedAffiliate.value;
        const gateway = this.selectedGateway.value;
        if (affiliate && gateway) {
            this.spinner.show("gateway");
            this.BillingCustomerService.getGatewaySetting(
                this.authService.getClaimsUserId(),
                affiliate.id,
                gateway as BillingProviderEnumDTO
            ).subscribe(
                (settings) => {
                    this.customerGatewaySettings = settings;
                    this.spinner.hide("gateway");
                },
                () => this.spinner.hide("gateway")
            );
        }
    }

    listAffiliates(search: string): void {
        this.spinner.show("search");
        const filter: AffiliatesFilterDTO = {
            active: true,
            find: search,
            orderBy: "nome",
            skip: 0,
            take: 50,
            sortOrder: "asc",
            providerDataNotSynced: null
        };

        this.AffiliateService.list(filter)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (res) => {
                    this.affiliates = res?.data ?? [];
                    this.spinner.hide("search");
                },
                () => this.spinner.hide("search")
            );
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.selectedAffiliate.complete();
        this.selectedGateway.complete();
    }

    deleteCard(cardExternalId: string): void {
        const config: MatDialogConfig<ConfirmDialogData> = {
            data: {
                title: this.translate.instant("PAYMENT_METHODS.DELETE_CARD.TITLE"),
                msg: this.translate.instant("PAYMENT_METHODS.DELETE_CARD.MSG")
            }
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (!result) return;
                this.BillingCustomerService.removeCreditCard(
                    this.authService.getClaimsUserId(),
                    this.selectedGateway.value,
                    cardExternalId,
                    this.selectedAffiliate.value.id
                )
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((x: CustomerGatewaySettingDTO) => {
                        this.customerGatewaySettings = x;
                    });
            });
    }

    setAsDefault(cardExternalId: string): void {
        const config: MatDialogConfig<ConfirmDialogData> = {
            data: {
                title: this.translate.instant("PAYMENT_METHODS.SET_AS_DEFAULT.TITLE"),
                msg: this.translate.instant("PAYMENT_METHODS.SET_AS_DEFAULT.MSG"),
                mandatoryConfirmationPhrase: false
            }
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (!result) return;
                this.BillingCustomerService.setAsDefault(
                    this.authService.getClaimsUserId(),
                    this.selectedGateway.value,
                    cardExternalId,
                    this.selectedAffiliate.value.id
                )
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((x: CustomerGatewaySettingDTO) => {
                        this.customerGatewaySettings = x;
                    });
            });
    }

    addNewCard(): void {
        const newPaymentNewCreditCardDialogComponentRef = this.dialog.open(NewCreditCardDialogComponent);

        // Subscreve ao evento afterClosed do diálogo
        newPaymentNewCreditCardDialogComponentRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result: CreditCardData) => {
                if (!result) {
                    return;
                }

                this.tokenize(result)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(
                        () => {},
                        (error) => {
                            const config: MatDialogConfig<ConfirmDialogData> = {
                                data: {
                                    title: this.translate.instant("PAYMENT_METHODS.ADD_CREDIT_CARD_DIALOG.TITLE"),
                                    msg: this.translate.instant("PAYMENT_METHODS.ADD_CREDIT_CARD_DIALOG.MSG") + error
                                }
                            };

                            const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
                        }
                    );
            });
    }

    tokenize(creditCardData: CreditCardData): Observable<CustomerGatewaySettingDTO> {
        switch (this.selectedGateway.value) {
            case BillingProviderEnumDTO.Iugu:
                return this.tokenizeIugu(creditCardData).pipe(
                    takeUntil(this._unsubscribeAll),
                    tap((out: CustomerGatewaySettingDTO) => {
                        this.customerGatewaySettings = out;
                    })
                );
            case BillingProviderEnumDTO.Vindi:
                return this.tokenizeVindi(creditCardData).pipe(
                    takeUntil(this._unsubscribeAll),
                    tap((out: CustomerGatewaySettingDTO) => {
                        this.customerGatewaySettings = out;
                    })
                );
            case BillingProviderEnumDTO.Asaas:
                return this.tokenizeAsaas(creditCardData).pipe(
                    takeUntil(this._unsubscribeAll),
                    tap((out: CustomerGatewaySettingDTO) => {
                        this.customerGatewaySettings = out;
                    })
                );
            default:
                throw new Error(`Method not implemented for provider ${this.selectedGateway.value}.`);
        }
    }

    tokenizeAsaas(creditCardData: CreditCardData): Observable<CustomerGatewaySettingDTO> {
        return this.BillingCustomerService.get(this.authService.getClaimsUserId()).pipe(
            takeUntil(this._unsubscribeAll),
            mergeMap((customer) => {
                const addressCustomer = customer.address;

                const asaasTokenizeModel: AsaasTokenizeModel = {
                    affiliateId: this.selectedAffiliate.value.id,
                    creditCard: {
                        ccv: creditCardData.cvc,
                        expiryMonth: creditCardData.expiry.substring(0, 2),
                        expiryYear: "20" + creditCardData.expiry.substring(2),
                        holderName: creditCardData.name,
                        number: creditCardData.number,
                        creditCardHolderInfo: {
                            addressComplement: addressCustomer.additionalDetails,
                            addressNumber: addressCustomer.number,
                            cpfCnpj: customer.brazilianData.cpfCNPJ,
                            email: customer.email,
                            mobilePhone: null,
                            name: customer.name,
                            postalCode: addressCustomer.zipcode,
                            phone: creditCardData.phone,
                            remoteIp: "localhost" //vai ser buscado no backend
                        }
                    }
                };

                return this.BillingCustomerService.tokenizeAsaasCreditCard(
                    this.authService.getClaimsUserId(),
                    asaasTokenizeModel
                );
            }),
            mergeMap((tokenized: CreditCardResponse) => {
                const creditCardCreateDTO = {
                    displayNumber: tokenized.creditCardNumber,
                    gatewayToken: tokenized.creditCardToken,
                    holderName: creditCardData.name,
                    issuer: this.getIssuer(tokenized.creditCardBrand),
                    month: Number(creditCardData.expiry.substring(0, 2)),
                    year: Number("20" + creditCardData.expiry.substring(2))
                } as CreditCardCreateDTO;

                return this.BillingCustomerService.insertCreditCard(
                    this.authService.getClaimsUserId(),
                    this.selectedGateway.value,
                    this.selectedAffiliate.value.id,
                    creditCardCreateDTO
                );
            })
        );
    }

    tokenizeIugu(creditCardData: CreditCardData): Observable<CustomerGatewaySettingDTO> {
        const nameArray = creditCardData.name.split(" ");
        const surName = nameArray.pop();

        return this._apiIuguService
            .tokenize({
                cardExpirationMonth: creditCardData.expiry.substring(0, 2),
                cardExpirationYear: "20" + creditCardData.expiry.substring(2),
                cardNumber: creditCardData.number,
                securityCode: creditCardData.cvc,
                firstName: nameArray.join(" "),
                surName: surName
            } as IuguCreditCard)
            .pipe(
                takeUntil(this._unsubscribeAll),
                mergeMap((tokenized: IuguResponse) => {
                    const creditCardCreateDTO = {
                        displayNumber: tokenized.extra_info.display_number,
                        gatewayToken: tokenized.id,
                        holderName: tokenized.extra_info.holder_name,
                        issuer: this.getIssuer(tokenized.extra_info.brand),
                        month: tokenized.extra_info.month,
                        year: tokenized.extra_info.year,
                        isDefault: creditCardData.isDefault
                    } as CreditCardCreateDTO;

                    return this.BillingCustomerService.insertCreditCard(
                        this.authService.getClaimsUserId(),
                        this.selectedGateway.value,
                        this.selectedAffiliate.value.id,
                        creditCardCreateDTO
                    );
                }),
                catchError((error: HttpErrorResponse) => {
                    throw HttpErrorResponseUtils.FormatErrorMessage(error);
                })
            );
    }

    tokenizeVindi(creditCardData: any): Observable<CustomerGatewaySettingDTO> {
        throw new Error("Method not implemented.");
    }

    getIssuer(brand: string): CreditCardIssuerDTO | null {
        const formatedCode = brand?.toLowerCase()?.replace("_", "")?.replace("-", "")?.replace(" ", "");

        switch (formatedCode) {
            case "master":
            case "mastercard":
                return CreditCardIssuerDTO.Mastercard;
            case "hiper":
            case "hipercard":
                return CreditCardIssuerDTO.HiperCard;
            case "elo":
                return CreditCardIssuerDTO.Elo;
            case "dinersclub":
                return CreditCardIssuerDTO.DinersClub;
            case "americanexpress":
            case "amex":
                return CreditCardIssuerDTO.Amex;
            case "visa":
                return CreditCardIssuerDTO.Visa;
            default:
                return null;
        }
    }
}
